import { useMutation, useQueryClient } from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import { fetchSuggestedLocations } from "@/api/geo-code";
import { PROFILE_KEY } from "@/api/query-keys";
import type { OnboardUser } from "@/domains/auth/api/type";
import type { JobSubcategoryKey } from "@/domains/freelance/taxonomy/types";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import { boundSkillsCsv } from "@/libs/skill-input";
import { tryCatch } from "@/libs/try-catch";
import { clearOnboardingStore } from "../context/OnboardingContext";

const onboardUser = async (payload: OnboardUser) => {
  const profile =
    typeof payload.profile.skills === "string"
      ? {
          ...payload.profile,
          skills: boundSkillsCsv(payload.profile.skills) ?? "",
        }
      : payload.profile;
  await axiosInstance.post(`/profile/onboarding`, {
    profile,
    education: payload.education,
    jobs: payload.jobs,
  });

  return {
    profile_id: payload.profile.id,
  };
};

export const useOnboardUser = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: onboardUser,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
      await clearOnboardingStore();
    },
  }));
};

export type OnboardByCVPayload = {
  cv: File;
};

type Language = {
  en: string | null;
  it: string | null;
};

type SocialMedia = {
  linkedin: string | null;
  github: string | null;
};

type Experience = {
  company: string;
  position: string;
  start_date: string;
  end_date: string | null;
  description: string;
};

type Education = {
  school: string;
  degree: string;
  start_date: string;
  end_date: string;
  description: string;
};

/**
 * Taxonomy proposal carried by the CV/LinkedIn analysis (BE-REQUIREMENT-PR3 §4.4),
 * in one of its two forms: no proposal (all three `null`), or a proposal with a
 * primary category, an optional secondary one and its subcategory keys.
 * The prefill that reads it is out of scope here (spec 3).
 */
export type ProfileTaxonomyProposal =
  | {
      job_category: null;
      job_category_secondary: null;
      job_subcategories: null;
    }
  | {
      job_category: JobCategory;
      job_category_secondary: JobCategory | null;
      job_subcategories: JobSubcategoryKey[];
    };

export type CVAnalysis = {
  avatar_url: string | null;
  full_name: string;
  birthdate: string | null;
  email: string | null;
  website: string | null;
  nationality: string | null;
  portfolio: string | null;
  residency: string | null;
  skills: string[];
  languages: Language;
  price_per_day: string | null;
  job_title: string | null;
  socialMedia: SocialMedia;
  bio: string | null;
  experience: Experience[];
  education: Education[];
} & ProfileTaxonomyProposal;

type CVAnalysisResponse = {
  msg: string;
  analysis: CVAnalysis;
};

const onboardByCV = async (payload: OnboardByCVPayload) => {
  const formData = new FormData();
  formData.append("cv", payload.cv);
  const { data } = await axiosInstance.post<CVAnalysisResponse>(
    "/profile/cv-analyzer/",
    formData,
  );

  let location: string | null = null;
  if (data.analysis.residency) {
    const [suggestedLocations, error] = await tryCatch(
      fetchSuggestedLocations(data.analysis.residency),
    );
    if (error) return data;
    if (suggestedLocations) {
      const firstLocation = suggestedLocations.features?.[0];
      if (firstLocation) {
        const firstLocationCity = firstLocation.properties.city ?? "";
        const firstLocationCountry = firstLocation.properties.country ?? "";
        if (firstLocationCity && firstLocationCountry) {
          const simplifiedLocation = `${firstLocationCity}, ${firstLocationCountry}`;
          location = simplifiedLocation;
        } else {
          location = null;
        }
      }
    }
  } else {
    location = null;
  }
  return {
    analysis: {
      ...data.analysis,
      residency: location,
    },
  };
};

export const useOnboardByCV = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: onboardByCV,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};

const onboardByResume = async (payload: OnboardByCVPayload) => {
  const formData = new FormData();
  formData.append("cv", payload.cv);

  const [data, cvError] = await tryCatch(onboardByCV(payload));

  if (cvError) {
    throw cvError;
  }

  return {
    analysis: data.analysis,
  };
};

export const useOnboardByResume = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: onboardByResume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
    },
  }));
};
